/* ──────────────────────────────────────────────────────────────────────────
   Where the admin lives.

   Lifted from numero-links apps/admin/lib/paths.ts on 2026-09-28. There the
   console was mounted at /dashboard on every creator hostname. In Signet it is
   a module: its pages live under /links and its API under /api/m/links, so
   the lifted screens keep writing the paths they always wrote ("/creators",
   "/api/links", "/i/...") and this file is the one place that knows where
   those now are.
   ────────────────────────────────────────────────────────────────────────── */

/** The module's page prefix in Signet's router. */
export const BASE_PATH = "/links";
/** The module's API prefix. Every route there is behind guard(). */
export const API_BASE = "/api/m/links";

/**
 * An admin path, as the lifted screens write it, to where it is in Signet.
 *
 * The admin's own home ("/") was its overview; in Signet the module's home is
 * the links list, which is what people open it for, so the overview moved to
 * /links/overview. Idempotent, so a path that already went through here is
 * left alone.
 */
export function adminPath(path: string): string {
  if (!path.startsWith("/")) path = `/${path}`;
  if (path.startsWith(`${API_BASE}/`) || path === API_BASE) return path;
  if (path.startsWith("/api/")) return `${API_BASE}${path.slice(4)}`;
  if (path.startsWith("/i/")) return `${API_BASE}${path}`;
  if (path === "/" || path.startsWith("/?")) return `${BASE_PATH}/overview${path.slice(1)}`;
  if (path === BASE_PATH || path.startsWith(`${BASE_PATH}/`) || path.startsWith(`${BASE_PATH}?`)) return path;
  return `${BASE_PATH}${path}`;
}

/** The inverse: what the lifted screens would call this path. */
export function appPath(path: string): string {
  if (path === `${BASE_PATH}/overview`) return "/";
  if (path.startsWith(`${BASE_PATH}/`)) return path.slice(BASE_PATH.length);
  return path;
}

/**
 * A path that is safe to send a browser to after signing in.
 *
 * Anything that is not a single-slash, same-origin path becomes the root —
 * `//evil.example` included, which a browser reads as protocol-relative and
 * would otherwise turn a `?next=` into an open redirect.
 */
export function safeNext(next: string | undefined | null): string {
  if (!next || !next.startsWith("/") || next.startsWith("//") || next.startsWith("/\\")) return "/";
  return next;
}

/** The admin's own copy of a stored image, at a given width. */
export function mediaSrc(ref: string, width: number, format: "jpg" | "webp" | "avif" = "jpg"): string {
  return `${API_BASE}/i/${ref}-${width}.${format}`;
}

/**
 * A creator's photograph: their own, or the one their links are using.
 *
 * `avatarFromLink` is filled in when the workspace is built and is never
 * written back, so a creator who has not uploaded anything wears the picture
 * from their most recently updated link and loses it again when that link
 * changes or goes. Every screen that draws a creator goes through here, so
 * they cannot disagree about which photograph is theirs.
 */
export function creatorPhoto(
  creator: { avatar: string | null; avatarFromLink?: string | null },
  width: number
): string | null {
  const ref = creator.avatar ?? creator.avatarFromLink ?? null;
  return ref ? mediaSrc(ref, width) : null;
}

/**
 * The photograph a link wears in a list.
 *
 * Its own, and failing that the creator's.
 *
 * A direct link has no landing page, which means it has nowhere to put a
 * profile picture and its own is null forever. Drawing nothing there said the
 * creator had no photograph, when what was true is that this particular link
 * has no page to put one on. The fallback is the same one a creator card
 * already makes in the other direction: a creator with no picture borrows one
 * from a link, and now a link with no picture borrows one from its creator.
 */
export function linkPhoto(
  link: { config: { avatar?: string | null } },
  creator: { avatar: string | null; avatarFromLink?: string | null } | null | undefined,
  width: number
): string | null {
  if (link.config.avatar) return mediaSrc(link.config.avatar, width);
  return creator ? creatorPhoto(creator, width) : null;
}
